import React, { useState, useEffect } from 'react';
import QuickCustomerForm from './QuickCustomerForm';
import QuickVehicleForm from './QuickVehicleForm';
import QuickServiceForm from './QuickServiceForm';
import QuickPartSelector from './QuickPartSelector';
import PartForm from './PartForm';

const QuoteForm = ({ quote, customers, vehicles, services, parts, settings, onSubmit, onCancel, loading, onCreateCustomer, onCreateVehicle, onCreateService, onCreatePart, defaultCustomerId }) => {
  const [formData, setFormData] = useState({
    quote_number: '',
    customer_id: '',
    vehicle_id: '',
    status: 'draft',
    valid_until: '',
    notes: '',
    items: [],
    discount_type: '', // 'percent' | 'fixed'
    discount_value: '', // Initialize as empty string or number
    discount_label: '',
    tax_enabled: true,
    payment_config: {
      allow_full: true,
      allow_half: true,
      allow_installments: true,
      installments_deposit_percent: 20,
      installments_max_count: 12
    }
  });

  const [errors, setErrors] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_total: 0,
    tax_amount: 0,
    total: 0
  });

  // États pour la création client rapide
  const [showQuickCustomerForm, setShowQuickCustomerForm] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // États pour la création véhicule rapide
  const [showQuickVehicleForm, setShowQuickVehicleForm] = useState(false);
  const [creatingVehicle, setCreatingVehicle] = useState(false);

  // États pour la création service rapide
  const [showQuickServiceForm, setShowQuickServiceForm] = useState(false);
  const [creatingService, setCreatingService] = useState(false);

  // Auto-generate invoice
  const [autoGenerateInvoice, setAutoGenerateInvoice] = useState(false);

  // États pour le sélecteur de pièces
  const [showPartSelector, setShowPartSelector] = useState(false);

  // États pour la création de pièce rapide
  const [showQuickPartForm, setShowQuickPartForm] = useState(false);
  const [creatingPart, setCreatingPart] = useState(false);

  // Éditeur de ligne (popup synchronisé avec le tableau)
  const [showItemEditor, setShowItemEditor] = useState(false);
  const [activeItemId, setActiveItemId] = useState(null);
  const [editorIsNewItem, setEditorIsNewItem] = useState(false);

  const statuses = [
    { value: 'draft', label: 'Brouillon', color: '#6b7280' },
    { value: 'sent', label: 'Envoyé', color: '#3b82f6' },
    { value: 'accepted', label: 'Accepté', color: '#10b981' },
    { value: 'rejected', label: 'Refusé', color: '#ef4444' },
    { value: 'expired', label: 'Expiré', color: '#f59e0b' }
  ];

  useEffect(() => {
    if (quote) {
      const validUntil = (() => {
        if (!quote.valid_until) return '';
        const d = new Date(quote.valid_until);
        return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      })();
      setFormData({
        quote_number: quote.quote_number || '',
        customer_id: quote.customer_id || '',
        vehicle_id: quote.vehicle_id || '',
        status: quote.status || 'draft',
        valid_until: validUntil,
        notes: quote.notes || '',
        items: quote.items || [],
        discount_type: quote.discount_type || '',
        discount_value: Number(quote.discount_value || 0),
        discount_label: quote.discount_label || '',
        tax_enabled: typeof quote.tax_enabled === 'boolean' ? quote.tax_enabled : true,
        payment_config: quote.payment_config || {
          allow_full: true,
          allow_half: true,
          allow_installments: true,
          installments_deposit_percent: 20,
          installments_max_count: 12
        }
      });

      if (quote.customer_id) {
        const customer = customers.find(c => c.id === quote.customer_id);
        setSelectedCustomer(customer);
        updateAvailableVehicles(quote.customer_id);
      }
    } else {
      // Generate quote number for new quote
      if (settings) {
        const nextNumber = `${settings.quote_prefix}-${String(settings.quote_counter).padStart(4, '0')}`;
        setFormData(prev => ({ ...prev, quote_number: nextNumber }));
      }

      // Set default valid until date (30 days from now or settings value)
      const validityDays = Number(settings?.quote_validity_days) || 30;
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + validityDays);
      const validUntilStr = (!isNaN(validUntil.getTime())) ? validUntil.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        valid_until: validUntilStr
      }));

      // Prefill customer if provided
      if (defaultCustomerId) {
        setFormData(prev => ({ ...prev, customer_id: defaultCustomerId, vehicle_id: '' }));
        const customer = customers.find(c => c.id === defaultCustomerId);
        setSelectedCustomer(customer || null);
        updateAvailableVehicles(defaultCustomerId);
      }
    }
  }, [quote, customers, settings, defaultCustomerId]);

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.discount_type, formData.discount_value, formData.tax_enabled, settings]);

  const updateAvailableVehicles = (customerId) => {
    const customerProjects = vehicles.filter(v => {
      const link = v.client || v.customer_id || v.customer;
      return link === customerId;
    });
    setAvailableVehicles(customerProjects);
  };

  // Keep available vehicles in sync if vehicles list changes after a customer is selected
  useEffect(() => {
    if (formData.customer_id) {
      updateAvailableVehicles(formData.customer_id);
    }
  }, [vehicles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : value // Allow empty string for number fields
    }));

    if (name === 'customer_id') {
      const customer = customers.find(c => c.id === value);
      setSelectedCustomer(customer);
      updateAvailableVehicles(value);
      setFormData(prev => ({ ...prev, vehicle_id: '' }));
    }

    // Clear error for the changed field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePaymentConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payment_config: {
        ...(prev.payment_config || {}),
        [field]: value === '' ? '' : value
      }
    }));
  };

  const addItem = () => {
    const newId = Date.now();
    const newItem = {
      id: newId,
      service_id: '',
      description: '',
      quantity: 1,
      unit_price: '',
      tax_rate: settings?.default_tax_rate || 20,
      total: 0
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    return newId;
  };

  const addAndEditItem = () => {
    const id = addItem();
    setActiveItemId(id);
    setShowItemEditor(true);
    setEditorIsNewItem(true);
  };

  const openItemEditor = (itemId) => {
    setActiveItemId(itemId);
    setShowItemEditor(true);
    setEditorIsNewItem(false);
  };

  const updateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'service_id' && value) {
            const service = services.find(s => s.id === value);
            if (service) {
              updatedItem.description = service.name;
              updatedItem.unit_price = service.base_price;
            }
          }

          // Always recalculate total to ensure consistency
          const q = updatedItem.quantity === '' ? 0 : Number(updatedItem.quantity);
          const p = updatedItem.unit_price === '' ? 0 : Number(updatedItem.unit_price);
          updatedItem.total = q * p;

          return updatedItem;
        }
        return item;
      })
    }));
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (Number(item.quantity || 0) * Number(item.unit_price || 0)), 0);
    const type = formData.discount_type;
    const val = Number(formData.discount_value || 0);
    let discount = 0;
    if (type === 'percent') {
      discount = subtotal * (val / 100);
    } else if (type === 'fixed') {
      discount = val;
    }
    discount = Math.max(0, Math.min(discount, subtotal));

    const taxRate = (Number(settings?.default_tax_rate) || 20) / 100;
    const taxBase = subtotal - discount;
    const taxAmount = formData.tax_enabled ? (taxBase * taxRate) : 0;
    const total = taxBase + taxAmount;

    setTotals({ subtotal, discount_total: discount, tax_amount: taxAmount, total });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_id) newErrors.customer_id = 'Veuillez sélectionner un client';
    if (!formData.vehicle_id) newErrors.vehicle_id = 'Veuillez sélectionner un projet';
    if (!formData.valid_until) newErrors.valid_until = 'La date de validité est requise';
    if (formData.items.length === 0) newErrors.items = 'Ajoutez au moins une prestation';
    const itemErrors = formData.items.some(item => !item.description || item.quantity <= 0 || item.unit_price < 0);
    if (itemErrors) newErrors.items = 'Vérifiez les informations des prestations';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleQuickCustomerSubmit = async (customerData) => {
    setCreatingCustomer(true);
    try {
      const newCustomer = await onCreateCustomer(customerData);
      setFormData(prev => ({ ...prev, customer_id: newCustomer.id }));
      setSelectedCustomer(newCustomer);
      updateAvailableVehicles(newCustomer.id);
      setShowQuickCustomerForm(false);
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleQuickVehicleSubmit = async (vehicleData) => {
    setCreatingVehicle(true);
    try {
      const newProject = await onCreateVehicle({ ...vehicleData, client: formData.customer_id, customer_id: formData.customer_id });
      // Sélection immédiate et ajout local pour affichage sans refresh
      setFormData(prev => ({ ...prev, vehicle_id: newProject.id }));
      setAvailableVehicles(prev => (prev.some(p => p.id === newProject.id) ? prev : [...prev, newProject]));
      setShowQuickVehicleForm(false);
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
    } finally {
      setCreatingVehicle(false);
    }
  };

  const handleQuickServiceSubmit = async (serviceData) => {
    setCreatingService(true);
    try {
      await onCreateService(serviceData);
      setShowQuickServiceForm(false);
    } catch (error) {
      console.error('Erreur lors de la création du service:', error);
    } finally {
      setCreatingService(false);
    }
  };

  const handleQuickPartSubmit = async (partData) => {
    setCreatingPart(true);
    try {
      await onCreatePart(partData);
      setShowQuickPartForm(false);
    } catch (error) {
      console.error('Erreur lors de la création de la pièce:', error);
    } finally {
      setCreatingPart(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        ...formData,
        subtotal: totals.subtotal,
        discount_total: totals.discount_total,
        tax_amount: totals.tax_amount,
        total: totals.total,
        tax_rate: Number(settings?.default_tax_rate) || 20,
        autoGenerateInvoice: !quote && autoGenerateInvoice // Only for creation
      };
      onSubmit(submitData);
    }
  };

  const handleAddPart = (partItem) => {
    const newItem = { id: Date.now(), ...partItem };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
    setShowPartSelector(false);
  };

  /* Drag & Drop State */
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = (index) => {
    if (draggedItemIndex === null || draggedItemIndex === index) return;

    const newItems = [...formData.items];
    const itemToMove = newItems[draggedItemIndex];
    newItems.splice(draggedItemIndex, 1);
    newItems.splice(index, 0, itemToMove);

    setFormData(prev => ({ ...prev, items: newItems }));
    setDraggedItemIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  return (
    <div className="quote-form-overlay">
      <div className="quote-form-container">
        {/* ... */}
        <div className="quote-form-header">
          <h2>{quote ? 'Modifier le devis' : 'Nouveau devis'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="quote-form">
          {/* ... (General info section remains same) */}
          <div className="form-section">
            <h3>Informations générales</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote_number">Numéro de devis</label>
                <input type="text" id="quote_number" name="quote_number" value={formData.quote_number} onChange={handleChange} placeholder="DEV-0001" readOnly className="readonly" />
              </div>
              <div className="form-group">
                <label htmlFor="status">Statut</label>
                <select id="status" name="status" value={formData.status} onChange={handleChange}>
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="customer_id">Client *</label>
                <div className="select-with-add">
                  <select id="customer_id" name="customer_id" value={formData.customer_id} onChange={handleChange} className={errors.customer_id ? 'error' : ''}>
                    <option value="">Sélectionner un client...</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name} {customer.company && `(${customer.company})`}</option>
                    ))}
                  </select>
                  <button type="button" className="add-btn" onClick={() => setShowQuickCustomerForm(true)} title="Ajouter un nouveau client">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                  </button>
                </div>
                {errors.customer_id && <span className="error-text">{errors.customer_id}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="vehicle_id">Projet *</label>
                <div className="select-with-add">
                  <select id="vehicle_id" name="vehicle_id" value={formData.vehicle_id} onChange={handleChange} disabled={!formData.customer_id} className={errors.vehicle_id ? 'error' : ''}>
                    <option value="">Sélectionner un projet...</option>
                    {availableVehicles.map(p => (
                      <option key={p.id} value={p.id}>
                        {(p.name || p.brand || 'Projet')}{p.internal_code ? ` — ${p.internal_code}` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="add-btn" onClick={() => setShowQuickVehicleForm(true)} disabled={!formData.customer_id} title="Ajouter un nouveau projet">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                  </button>
                </div>
                {errors.vehicle_id && <span className="error-text">{errors.vehicle_id}</span>}
                {!formData.customer_id && (<span className="info-text">Sélectionnez d'abord un client pour ajouter un projet</span>)}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="valid_until">Valide jusqu'au *</label>
              <input type="date" id="valid_until" name="valid_until" value={formData.valid_until} onChange={handleChange} className={errors.valid_until ? 'error' : ''} />
              {errors.valid_until && <span className="error-text">{errors.valid_until}</span>}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>Prestations</h3>
              <div className="section-actions">
                <button type="button" onClick={addAndEditItem} className="btn-add-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>Ajouter une prestation</button>
                <button type="button" className="btn-create-service" onClick={() => setShowQuickServiceForm(true)} title="Créer une nouvelle prestation"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" /></svg>Nouvelle prestation</button>
                <button type="button" className="btn-add-part" onClick={() => setShowPartSelector(true)} title="Ajouter un produit du catalogue"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>Ajouter un produit</button>
                <button type="button" className="btn-create-part" onClick={() => setShowQuickPartForm(true)} title="Créer un nouveau produit"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" /></svg>Nouveau produit</button>
              </div>
            </div>

            {formData.items.length > 0 ? (
              <div className="items-table">
                <div className="table-header" style={{ gridTemplateColumns: '40px 200px 1fr 80px 120px 100px 60px' }}>
                  <div>#</div>
                  <div>Service</div>
                  <div>Description</div>
                  <div>Qté</div>
                  <div>Prix unitaire</div>
                  <div>Total</div>
                  <div>Actions</div>
                </div>
                {formData.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="table-row"
                    style={{
                      gridTemplateColumns: '40px 200px 1fr 80px 120px 100px 60px',
                      background: draggedItemIndex === index ? 'var(--admin-panel-2)' : 'transparent',
                      opacity: draggedItemIndex === index ? 0.5 : 1,
                      cursor: 'grab'
                    }}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="item-order" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab' }} title="Glisser pour réorganiser">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
                        <circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" />
                        <circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" />
                      </svg>
                    </div>
                    <div className="item-service">
                      <select value={item.service_id} onChange={(e) => updateItem(item.id, 'service_id', e.target.value)}>
                        <option value="">Service personnalisé</option>
                        {services.filter(s => s.active).map(service => (
                          <option key={service.id} value={service.id}>{service.name} ({service.base_price}€)</option>
                        ))}
                      </select>
                    </div>
                    <div className="item-description">
                      <input type="text" value={item.description} onChange={(e) => updateItem(item.id, 'description', e.target.value)} placeholder="Description de la prestation" />
                    </div>
                    <div className="item-quantity">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        min="0"
                        step="1"
                        inputMode="numeric"
                        className="no-spin"
                      />
                    </div>
                    <div className="item-price">
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(item.id, 'unit_price', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        className="no-spin"
                      />
                    </div>
                    <div className="item-total">{(Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2)}€</div>
                    <div className="item-actions">
                      <button type="button" onClick={() => removeItem(item.id)} className="btn-remove-item" title="Supprimer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-items">
                <p>Aucune prestation ajoutée</p>
                <button type="button" onClick={addItem} className="btn-primary">Ajouter la première prestation</button>
              </div>
            )}
            {errors.items && <span className="error-text">{errors.items}</span>}
          </div>

          {formData.items.length > 0 && (
            <div className="form-section">
              <h3>Récapitulatif</h3>
              <div className="form-row" style={{ marginBottom: 8 }}>
                <div className="form-group">
                  <label>Type de remise</label>
                  <select name="discount_type" value={formData.discount_type} onChange={handleChange}>
                    <option value="">Aucune</option>
                    <option value="percent">Pourcentage</option>
                    <option value="fixed">Montant fixe</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Valeur</label>
                  <input
                    type="number"
                    name="discount_value"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={handleChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                    inputMode="decimal"
                    placeholder={formData.discount_type === 'percent' ? 'Ex: 10 (%)' : 'Ex: 50 (€)'}
                    disabled={!formData.discount_type}
                    className="no-spin"
                  />
                </div>
                <div className="form-group">
                  <label>Intitulé (optionnel)</label>
                  <input type="text" name="discount_label" value={formData.discount_label} onChange={handleChange} placeholder="Ex: Promo" disabled={!formData.discount_type} />
                </div>
                <div className="form-group" style={{ alignSelf: 'end' }}>
                  <label style={{ display: 'block' }}>TVA applicable</label>
                  <input type="checkbox" checked={!!formData.tax_enabled} onChange={(e) => setFormData(prev => ({ ...prev, tax_enabled: e.target.checked }))} />
                </div>
              </div>
              <div className="totals-section">
                <div className="totals-row"><span>Sous-total HT :</span><span>{totals.subtotal.toFixed(2)}€</span></div>
                {totals.discount_total > 0 && (<div className="totals-row"><span>Remise{formData.discount_type === 'percent' ? ` (${Number(formData.discount_value || 0)}%)` : ''}{formData.discount_label ? ` - ${formData.discount_label}` : ''} :</span><span>-{totals.discount_total.toFixed(2)}€</span></div>)}
                {formData.tax_enabled ? (
                  <div className="totals-row"><span>TVA ({settings?.default_tax_rate || 20}%) :</span><span>{totals.tax_amount.toFixed(2)}€</span></div>
                ) : (
                  <div className="totals-row"><span>TVA non applicable</span><span>-</span></div>
                )}
                <div className="totals-row total"><span>Total TTC :</span><span>{totals.total.toFixed(2)}€</span></div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>Options de paiement client</h3>
            <div className="form-row" style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.payment_config?.allow_full !== false} onChange={(e) => handlePaymentConfigChange('allow_full', e.target.checked)} />
                <span>Comptant (1x)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.payment_config?.allow_half !== false} onChange={(e) => handlePaymentConfigChange('allow_half', e.target.checked)} />
                <span>50/50 (2x)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.payment_config?.allow_installments !== false} onChange={(e) => handlePaymentConfigChange('allow_installments', e.target.checked)} />
                <span>Échelonné</span>
              </label>
            </div>
            {formData.payment_config?.allow_installments !== false && (
              <div className="form-row" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label>Acompte pui échelonnement (%)</label>
                  <input
                    type="number"
                    value={formData.payment_config?.installments_deposit_percent || ''}
                    onChange={(e) => handlePaymentConfigChange('installments_deposit_percent', e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                    min="0"
                    max="100"
                    className="no-spin"
                  />
                </div>
                <div className="form-group">
                  <label>Max mensualités</label>
                  <input
                    type="number"
                    value={formData.payment_config?.installments_max_count || ''}
                    onChange={(e) => handlePaymentConfigChange('installments_max_count', e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                    min="2"
                    max="24"
                    className="no-spin"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Notes</h3>
            <div className="form-group">
              <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} placeholder="Notes sur le devis, conditions particulières..." rows="4" />
            </div>
          </div>

          <div className="form-actions" style={{ alignItems: 'center' }}>
            {!quote && (
              <label className="checkbox-label" style={{ marginRight: 'auto' }}>
                <input type="checkbox" checked={autoGenerateInvoice} onChange={(e) => setAutoGenerateInvoice(e.target.checked)} />
                <span className="checkbox-text" style={{ color: 'var(--admin-accent)', fontWeight: 500 }}>Générer la facture immédiatement</span>
              </label>
            )}
            <button type="button" onClick={onCancel} className="btn-cancel">Annuler</button>
            <button type="submit" disabled={loading} className="btn-submit">{loading ? 'Enregistrement...' : (quote ? 'Mettre à jour' : 'Créer le devis')}</button>
          </div>
        </form>

        {showItemEditor && (() => {
          const item = formData.items.find(i => i.id === activeItemId);
          if (!item) return null;
          const cancelItemEditor = () => {
            if (editorIsNewItem && activeItemId) {
              removeItem(activeItemId);
            }
            setShowItemEditor(false);
            setEditorIsNewItem(false);
          };
          return (
            <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) cancelItemEditor(); }}>
              <div className="modal-content" style={{ maxWidth: 720 }}>
                <div className="modal-header">
                  <h3>Éditer la prestation</h3>
                  <button className="close-btn" type="button" onClick={cancelItemEditor}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                </div>
                <div className="quick-service-form" style={{ padding: 20 }}>
                  <div className="form-row" style={{ gridTemplateColumns: '200px 1fr' }}>
                    <div className="form-group">
                      <label>Service</label>
                      <select value={item.service_id} onChange={(e) => updateItem(item.id, 'service_id', e.target.value)}>
                        <option value="">Service personnalisé</option>
                        {services.filter(s => s.active).map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.base_price}€)</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input type="text" value={item.description} onChange={(e) => updateItem(item.id, 'description', e.target.value)} placeholder="Description de la prestation" />
                    </div>
                  </div>
                  <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="form-group">
                      <label>Quantité</label>
                      <input
                        type="number"
                        value={item.quantity}
                        min="0"
                        step="1"
                        onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        inputMode="numeric"
                        className="no-spin"
                      />
                    </div>
                    <div className="form-group">
                      <label>Prix unitaire (€)</label>
                      <input
                        type="number"
                        value={item.unit_price}
                        min="0"
                        step="0.01"
                        onChange={(e) => updateItem(item.id, 'unit_price', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        inputMode="decimal"
                        className="no-spin"
                      />
                    </div>
                    <div className="form-group">
                      <label>Total</label>
                      <input type="text" value={(Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2) + '€'} readOnly className="readonly" />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={cancelItemEditor}>Annuler</button>
                    <button type="button" className="btn-primary" onClick={() => { setShowItemEditor(false); setEditorIsNewItem(false); }}>Valider</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <style>{`
          /* Generic modal styling for Quick* forms */
          .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
          .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
          .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 20px 0 20px; border-bottom: 1px solid var(--admin-border); margin-bottom: 16px; }
          .modal-header h3 { margin: 0; font-size: 1.2rem; font-weight: 600; color: var(--admin-text); }
          .modal-actions { display: flex; justify-content: flex-end; gap: 12px; padding: 0 20px 20px 20px; border-top: 1px solid var(--admin-border); margin-top: 16px; padding-top: 16px; }
          .btn-secondary { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-secondary:hover { background: rgba(255,255,255,0.06); }
          .btn-primary { background: var(--admin-accent); color: #fff; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-primary:disabled { opacity: .6; cursor: not-allowed; }

          .quote-form-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
          .quote-form-container { background: var(--admin-panel); color: var(--admin-text); border-radius: 12px; width: 100%; max-width: 1000px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.35); border: 1px solid var(--admin-border); }
          .quote-form-header { display: flex; justify-content: space-between; align-items: center; padding: 24px 24px 0 24px; border-bottom: 1px solid var(--admin-border); margin-bottom: 24px; position: sticky; top: 0; background: var(--admin-panel); z-index: 10; }
          .quote-form-header h2 { margin: 0; font-size: 1.5rem; font-weight: 600; color: var(--admin-text); }
          .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; transition: all 0.2s ease; }
          .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
          .quote-form { padding: 0 24px 24px 24px; }
          .form-section { margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--admin-border); }
          .form-section:last-of-type { border-bottom: none; }
          .form-section h3 { margin: 0 0 20px 0; font-size: 1.2rem; font-weight: 600; color: var(--admin-text); border-left: 4px solid var(--admin-accent); padding-left: 12px; }
          .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
          .section-header h3 { margin: 0; }
          .btn-add-item { background: #10b981; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.9rem; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 6px; }
          .btn-add-item:hover { background: #059669; }
          .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
          @media (max-width: 640px) { .form-row { grid-template-columns: 1fr; } }
          .form-group { position: relative; margin-bottom: 16px; }
          .form-group label { display: block; margin-bottom: 6px; font-weight: 500; color: var(--admin-text); font-size: 0.9rem; }
          .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 12px 16px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 8px; font-size: 0.9rem; transition: all 0.2s ease; box-sizing: border-box; }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
          .form-group input.error, .form-group select.error, .form-group textarea.error { border-color: #dc2626; }
          .form-group input.readonly { background: var(--admin-panel-2); color: var(--admin-muted); }
          .form-group textarea { resize: vertical; min-height: 100px; }
          .error-text { color: #dc2626; font-size: 0.8rem; margin-top: 4px; display: block; }
          .items-table { border: 1px solid var(--admin-border); border-radius: 8px; overflow: hidden; background: transparent; }
          .table-header { display: grid; grid-template-columns: 200px 1fr 80px 120px 100px 60px; background: var(--admin-panel-2); padding: 12px; font-weight: 600; color: var(--admin-text); font-size: 0.9rem; border-bottom: 1px solid var(--admin-border); }
          .table-row { display: grid; grid-template-columns: 200px 1fr 80px 120px 100px 60px; padding: 12px; border-bottom: 1px solid var(--admin-border); align-items: center; gap: 8px; }
          .table-row:last-child { border-bottom: none; }
          .item-service select, .item-description input, .item-quantity input, .item-price input { width: 100%; padding: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); border-radius: 4px; font-size: 0.85rem; }
          .item-total { font-weight: 600; color: var(--admin-accent-2); text-align: right; }
          .btn-remove-item { background: rgba(220,38,38,0.15); color: #dc2626; border: 1px solid rgba(220,38,38,0.3); border-radius: 4px; padding: 6px; cursor: pointer; transition: all 0.2s ease; }
          .btn-remove-item:hover { background: rgba(220,38,38,0.25); }
          .btn-edit-item { background: rgba(59,130,246,0.15); color: #3b82f6; border: 1px solid rgba(59,130,246,0.3); border-radius: 4px; padding: 6px; cursor: pointer; transition: all 0.2s ease; margin-right: 6px; }
          .btn-edit-item:hover { background: rgba(59,130,246,0.25); }
          .empty-items { text-align: center; padding: 40px; color: var(--admin-muted); }
          .empty-items p { margin: 0 0 16px 0; }
          .totals-section { background: var(--admin-panel-2); padding: 20px; border-radius: 8px; border: 1px solid var(--admin-border); }
          .totals-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 0.9rem; }
          .totals-row.total { font-weight: 600; font-size: 1.1rem; color: var(--admin-text); padding-top: 8px; border-top: 1px solid var(--admin-border); margin-top: 8px; }
          .form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--admin-border); position: sticky; bottom: 0; background: var(--admin-panel); }
          .btn-cancel, .btn-submit { padding: 12px 24px; border-radius: 8px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; border: none; font-size: 0.9rem; }
          .btn-cancel { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); }
          .btn-cancel:hover { background: rgba(255,255,255,0.06); }
          .btn-submit { background: var(--admin-accent); color: #fff; }
          .btn-submit:hover:not(:disabled) { filter: brightness(0.95); }
          .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
          .select-with-add { display: flex; gap: 8px; align-items: flex-start; }
          .select-with-add select { flex: 1; }
          .add-btn { background: #10b981; color: #fff; border: none; border-radius: 6px; padding: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; flex-shrink: 0; height: 42px; width: 42px; }
          .add-btn:hover { background: #059669; transform: scale(1.05); }
          .add-btn:active { transform: scale(0.95); }
          .add-btn:disabled { background: #9ca3af; cursor: not-allowed; transform: none; }
          .add-btn:disabled:hover { background: #9ca3af; transform: none; }
          .info-text { color: var(--admin-muted); font-size: 0.8rem; margin-top: 4px; font-style: italic; }
          .quick-customer-modal, .quick-vehicle-modal, .quick-service-modal { max-width: 800px; width: 90vw; }
          .quick-customer-form { padding: 24px; }
          .quick-customer-form .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin-bottom: 24px; }
          .quick-customer-form .full-width { grid-column: 1 / -1; }
          .quick-vehicle-form { padding: 24px; }
          .quick-vehicle-form .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 24px; }
          .section-actions { display: flex; gap: 12px; align-items: center; }
          .btn-create-service { background: #8b5cf6; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.875rem; font-weight: 500; transition: all 0.2s ease; }
          .btn-create-service:hover { background: #7c3aed; transform: translateY(-1px); }
          .btn-create-service:active { transform: translateY(0); }
          .btn-add-part { background: #f59e0b; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.875rem; font-weight: 500; transition: all 0.2s ease; }
          .btn-add-part:hover { background: #d97706; transform: translateY(-1px); }
          .btn-add-part:active { transform: translateY(0); }
          .btn-create-part { background: #10b981; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.875rem; font-weight: 500; transition: all 0.2s ease; }
          .btn-create-part:hover { background: #059669; transform: translateY(-1px); }
          .btn-create-part:active { transform: translateY(0); }
          .quick-service-form { padding: 24px; }
          .quick-service-form .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin-bottom: 32px; }
          .quick-service-form .full-width { grid-column: 1 / -1; }
          .quick-customer-form .form-actions, .quick-vehicle-form .form-actions, .quick-service-form .form-actions { display: flex; justify-content: flex-end; gap: 12px; padding-top: 20px; border-top: 1px solid var(--admin-border); margin-top: 24px; }
          .quick-customer-form .btn-secondary, .quick-vehicle-form .btn-secondary, .quick-service-form .btn-secondary, .quick-customer-form .btn-primary, .quick-vehicle-form .btn-primary, .quick-service-form .btn-primary { padding: 10px 20px; border-radius: 6px; font-weight: 500; font-size: 0.875rem; cursor: pointer; transition: all 0.2s ease; border: none; }
          .quick-service-form textarea { padding: 10px 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 6px; font-size: 0.875rem; font-family: inherit; resize: vertical; min-height: 80px; }
          .quick-service-form textarea:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
          .checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.875rem; }
          .checkbox-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
          .checkbox-text { color: var(--admin-text); font-weight: 500; }
          .quick-customer-form .form-group, .quick-vehicle-form .form-group, .quick-service-form .form-group { margin-bottom: 4px; }
          .quick-customer-form label, .quick-vehicle-form label, .quick-service-form label { display: block; margin-bottom: 6px; font-weight: 500; color: var(--admin-text); font-size: 0.875rem; }
          .quick-customer-form input, .quick-vehicle-form input, .quick-service-form input, .quick-customer-form select, .quick-vehicle-form select, .quick-service-form select { width: 100%; padding: 10px 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 6px; font-size: 0.875rem; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
          .quick-customer-form input:focus, .quick-vehicle-form input:focus, .quick-service-form input:focus, .quick-customer-form select:focus, .quick-vehicle-form select:focus, .quick-service-form select:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
          .quick-customer-form .error-message, .quick-vehicle-form .error-message, .quick-service-form .error-message { color: #ef4444; font-size: 0.75rem; margin-top: 4px; display: block; }
          @media (max-width: 768px) {
            .table-header, .table-row { grid-template-columns: 1fr; gap: 8px; }
            .table-header { display: none; }
            .table-row { display: flex; flex-direction: column; padding: 16px; }
            .quick-customer-modal, .quick-vehicle-modal, .quick-service-modal { width: 95vw; margin: 20px auto; }
            .quick-customer-form, .quick-vehicle-form, .quick-service-form { padding: 16px; }
            .quick-customer-form .form-grid, .quick-vehicle-form .form-grid, .quick-service-form .form-grid { grid-template-columns: 1fr; gap: 16px; }
            .section-actions { flex-direction: column; gap: 8px; align-items: stretch; }
            .btn-create-service { justify-content: center; }
          }

          /* Hide spin buttons */
          input[type=number].no-spin::-webkit-inner-spin-button, 
          input[type=number].no-spin::-webkit-outer-spin-button { 
            -webkit-appearance: none; 
            margin: 0; 
          }
          input[type=number].no-spin {
            -moz-appearance: textfield;
          }
        `}</style>
      </div>

      {showQuickCustomerForm && (
        <QuickCustomerForm onSubmit={handleQuickCustomerSubmit} onCancel={() => setShowQuickCustomerForm(false)} loading={creatingCustomer} />
      )}
      {showQuickVehicleForm && (
        <QuickVehicleForm customerId={formData.customer_id} onSubmit={handleQuickVehicleSubmit} onCancel={() => setShowQuickVehicleForm(false)} loading={creatingVehicle} />
      )}
      {showQuickServiceForm && (
        <QuickServiceForm onSubmit={handleQuickServiceSubmit} onCancel={() => setShowQuickServiceForm(false)} loading={creatingService} />
      )}
      {showPartSelector && (
        <QuickPartSelector parts={parts || []} onSubmit={handleAddPart} onCancel={() => setShowPartSelector(false)} loading={false} />
      )}
      {showQuickPartForm && (
        <PartForm onSave={handleQuickPartSubmit} onCancel={() => setShowQuickPartForm(false)} loading={creatingPart} />
      )}
    </div>
  );
};

export default QuoteForm;
